import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SaveOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Paragraph, Text } = Typography;

type DraftRow = {
  employee_id: string;
  employee_label: string;
  opening_balance: number;
  carried_forward: number;
  consumed: number;
  available_days?: number;
  existing?: boolean;
};

/**
 * Go-live / cutover: set leave opening balances for the company leave year.
 * Available = opening + accrued + carried − consumed − encashed − pending
 * (accrued/pending/encashed stay system-managed; opening/carried/consumed editable here).
 */
const LeaveOpeningBalancesPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [filterForm] = Form.useForm();
  const [year, setYear] = useState(dayjs().year());
  const [leaveType, setLeaveType] = useState('ANNUAL');
  const [draft, setDraft] = useState<Record<string, DraftRow>>({});
  const [saving, setSaving] = useState(false);
  const { options: empOptions, empLabel } = useEmployeeOptions();

  const { data: leaveTypes = [] } = useQuery({
    queryKey: hrmsKeys.leaveTypes(),
    queryFn: () => hrmsService.listLeaveTypes(),
  });

  const { data: balances = [], isLoading, refetch } = useQuery({
    queryKey: [...hrmsKeys.all, 'leave-balances-company', year, leaveType],
    queryFn: () => hrmsService.listCompanyLeaveBalances({ leave_year: year, leave_type: leaveType }),
  });

  const leaveTypeOptions = useMemo(
    () =>
      leaveTypes.length
        ? leaveTypes.map((t: any) => ({ value: t.leave_code, label: `${t.leave_name} (${t.leave_code})` }))
        : [{ value: 'ANNUAL', label: 'Annual (ANNUAL)' }],
    [leaveTypes]
  );

  // Merge active employees + existing balances into editable grid
  const rows: DraftRow[] = useMemo(() => {
    const byEmp = new Map<string, any>();
    for (const b of balances) byEmp.set(b.employee_id, b);
    const list: DraftRow[] = [];
    for (const o of empOptions) {
      const existing = byEmp.get(o.value);
      const override = draft[o.value];
      list.push({
        employee_id: o.value,
        employee_label: o.label,
        opening_balance: override?.opening_balance ?? Number(existing?.opening_balance || 0),
        carried_forward: override?.carried_forward ?? Number(existing?.carried_forward || 0),
        consumed: override?.consumed ?? Number(existing?.consumed || 0),
        available_days: existing?.available_days,
        existing: !!existing,
      });
    }
    return list;
  }, [empOptions, balances, draft]);

  const setField = (employeeId: string, field: keyof DraftRow, value: number) => {
    setDraft((prev) => {
      const base =
        prev[employeeId] ||
        rows.find((r) => r.employee_id === employeeId) ||
        ({
          employee_id: employeeId,
          employee_label: empLabel(employeeId),
          opening_balance: 0,
          carried_forward: 0,
          consumed: 0,
        } as DraftRow);
      return { ...prev, [employeeId]: { ...base, [field]: value } };
    });
  };

  const applyFilter = async () => {
    const v = await filterForm.validateFields();
    setYear(Number(v.leave_year));
    setLeaveType(v.leave_type);
    setDraft({});
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const payloadRows = rows
        .filter((r) => draft[r.employee_id] || r.existing || r.opening_balance || r.carried_forward || r.consumed)
        .map((r) => ({
          employee_id: r.employee_id,
          opening_balance: Number(r.opening_balance) || 0,
          carried_forward: Number(r.carried_forward) || 0,
          consumed: Number(r.consumed) || 0,
        }))
        .filter((r) => draft[r.employee_id] || r.opening_balance || r.carried_forward || r.consumed);

      // Prefer only dirty rows
      const dirtyIds = new Set(Object.keys(draft));
      const toSave = payloadRows.filter((r) => dirtyIds.has(r.employee_id));
      if (!toSave.length) {
        message.info('No changes to save');
        return;
      }
      const result = await hrmsService.bulkUpsertLeaveOpenings({
        leave_year: year,
        leave_type: leaveType,
        rows: toSave,
      });
      message.success(`Saved ${result?.saved ?? toSave.length} opening balance row(s)`);
      setDraft({});
      await refetch();
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<DraftRow> = [
    { title: 'Employee', dataIndex: 'employee_label', ellipsis: true },
    {
      title: 'Opening',
      dataIndex: 'opening_balance',
      width: 120,
      render: (v, r) => (
        <InputNumber
          min={0}
          value={v}
          style={{ width: '100%' }}
          onChange={(n) => setField(r.employee_id, 'opening_balance', Number(n) || 0)}
        />
      ),
    },
    {
      title: 'Carried in',
      dataIndex: 'carried_forward',
      width: 120,
      render: (v, r) => (
        <InputNumber
          min={0}
          value={v}
          style={{ width: '100%' }}
          onChange={(n) => setField(r.employee_id, 'carried_forward', Number(n) || 0)}
        />
      ),
    },
    {
      title: 'Already taken',
      dataIndex: 'consumed',
      width: 120,
      render: (v, r) => (
        <InputNumber
          min={0}
          value={v}
          style={{ width: '100%' }}
          onChange={(n) => setField(r.employee_id, 'consumed', Number(n) || 0)}
        />
      ),
    },
    {
      title: 'Available (system)',
      dataIndex: 'available_days',
      width: 120,
      render: (v) => (v != null ? Number(v).toFixed(2) : '—'),
    },
  ];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Leave opening balances"
          subtitle="Go-live cutover — set opening / carried / already-taken days per employee"
          primaryActions={
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveAll}>
              Save changes
            </Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="How balances are used"
        description={
          <span>
            Available days = Opening + Accrued + Carried − Consumed − Encashed − Pending. Opening balances entered
            here are used on leave apply/submit, ESS, liability report, and payroll unpaid-leave math. Configure
            Annual leave entitlement under <Text strong>Leave types</Text> and HRMS Setup.
          </span>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Form
          form={filterForm}
          layout="inline"
          initialValues={{ leave_year: year, leave_type: leaveType }}
          onFinish={applyFilter}
        >
          <Form.Item name="leave_year" label="Leave year" rules={[{ required: true }]}>
            <InputNumber min={2000} max={2100} />
          </Form.Item>
          <Form.Item name="leave_type" label="Leave type" rules={[{ required: true }]}>
            <Select style={{ width: 260 }} options={leaveTypeOptions} />
          </Form.Item>
          <Button type="primary" htmlType="submit">
            Load
          </Button>
        </Form>
        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          Showing {leaveType} for {year}. Edit cells then Save changes (only modified rows are written).
        </Paragraph>
      </Card>

      <Table
        rowKey="employee_id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        size="small"
      />
    </WorkspaceLayout>
  );
};

export default LeaveOpeningBalancesPage;
